if ( typeof debounce !== 'function' ) {
  window.debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
window.KEYCODES = {
  TAB: 9,
  ESC: 27,
  DOWN: 40,
  RIGHT: 39,
  UP: 38,
  LEFT: 37,
  RETURN: 13
};
window.KT_PREFIX = 'kt-';

window.KROWN = window.KROWN || {};
window.KROWN.previouslyFocused = null;
window.KROWN.functions = (() => {

  /* ---------------- IMAGE HELPERS ---------------- */

  const setImagesize = (img) => {
    let size = 0;
    if (img.offsetWidth / img.dataset.ratio < img.offsetHeight) {
      size = Math.ceil(img.offsetHeight * parseFloat(img.dataset.ratio));
    } else {
      size = Math.ceil(img.offsetWidth);
    }
    if ( size > 0 ) {
      img.setAttribute('sizes', `${size}px`);
    }
  };

  const resizeObserver = ('ResizeObserver' in window)
    ? new ResizeObserver(entries => {
        for (const entry of entries) setImagesize(entry.target);
      })
    : null;
    
  const handleImageLoad = img => {
    setImagesize(img);
    resizeObserver.observe(img);
  };

  /* ---------------- SCROLL PROGRESS CORE ---------------- */

  const progressElements = new Set();

  let ticking = false;
  let needsHeightSync = false;

  const updateScrollProgress = () => {
    
    if (needsHeightSync) {
      parallaxHeightPairs.forEach(({ target, ref }) => {
        target.style.height = `${ref.offsetHeight}px`;
      });
      needsHeightSync = false;
    }

    const viewportHeight = window.innerHeight;
  
    progressElements.forEach(({ el, callback, mode }) => {
      const rect = el.getBoundingClientRect();
      let progress = 0;
  
      if ( mode === 'enter' ) {
        // 0% when section top is at viewport bottom
        // 100% when section top is at viewport top
        // exception when the section is at the bottom of the page
        const vh = innerHeight;
        const max = document.documentElement.scrollHeight - vh;
        const top = rect.top + scrollY;
        const start = top - vh;
        progress = ((scrollY - start) * 100) / (Math.min(top, max) - start);
      } else if ( mode === 'cover' ) {
        // 0% when section is fully visible at top
        // 100% when section has fully scrolled past (bottom <= 0)
        const start = 0;
        const end = -(rect.height - viewportHeight);
        const scrolled = Math.min(Math.max(rect.top, end), start);
        progress = ((start - scrolled) * 100) / (start - end);
      } else {
        // default: 0% when section enters viewport,
        // 100% when fully scrolled through
        progress = ((viewportHeight - rect.top) * 100) / (rect.height + viewportHeight);
      }
  
      progress = Math.min(Math.max(progress, 0), 100);
      callback(progress, el);
    });
  
    ticking = false;
  };
  
  const onScrollOrResize = (e) => {
    if (e.type === 'resize') needsHeightSync = true;
    if (!ticking) {
      requestAnimationFrame(updateScrollProgress);
      ticking = true;
    }
  };

  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize, { passive: true });

  const observeScrollProgress = (el, callback, mode = 'full') => {
    const item = { el, callback, mode };
    progressElements.add(item);
    requestAnimationFrame(updateScrollProgress);
    return () => progressElements.delete(item);
  };

  /* ---------------- PARALLAX HANDLER ---------------- */

  const parallaxHeightPairs = [];
  
  const handleParallax = id => {
    const container = document.getElementById(id);
    if (!container) return;
  
    const start = parseFloat(container.dataset.jsParallaxStart) || 0;
    const end = parseFloat(container.dataset.jsParallaxEnd) || 0;
    const unit = container.dataset.jsParallaxUnit || 'px';
    const property = container.dataset.jsParallaxProperty || 'transform'; 
    const mode = container.dataset.jsParallaxMode || 'full';
    const targets = container.querySelectorAll('[data-js-parallax]');

    let scrollRef = container;

    targets.forEach(t => {
      const ref = t.closest('[data-js-parallax-height-reference]')
        || container.querySelector('[data-js-parallax-height-reference]');
      if (ref) {
        t.style.height = `${ref.offsetHeight}px`;
        parallaxHeightPairs.push({ target: t, ref });
        if (ref.hasAttribute('data-js-parallax-scroll-reference')) {
          scrollRef = ref;
        }
      }
    });
  
    observeScrollProgress(scrollRef, (progress) => {
      const value = start + (end - start) * (progress / 100);
  
      targets.forEach(t => {
        if (property === 'transform') {
          t.style.transform = `translateY(${value}${unit})`;
        } else if (property === 'backgroundSize') {
          t.style.backgroundSize = `${value}${unit} 100%`;
        } else {
          t.style[property] = `${value}${unit}`;
        }
      });
    }, mode);
  };

  /* ---------------- EVENT LISTENERS ---------------- */

  const eventDispatcher = (eventType, detail) => {
    document.dispatchEvent(new CustomEvent(eventType, {
      bubbles: true,
      detail: detail
    }));
  };

  const updateCart = (openDrawer = true) => {
    window.refreshCart(openDrawer);
  };
  const closeCartDrawer = () => {
    document.querySelector('[data-js-site-cart-sidebar]').hide();
  };
  const openCartDrawer = () => {
    document.querySelector('[data-js-site-cart-sidebar]').show();
  };
  
  return { handleImageLoad, setImagesize, resizeObserver, observeScrollProgress, handleParallax, eventDispatcher, updateCart, closeCartDrawer, openCartDrawer };

})();

document.addEventListener('krown:cart:error', (e) => {
  if ( document.body.classList.contains('template-cart') ) {
    window.scrollTo({
      top: document.querySelector('#AjaxCartForm .kt-alert') ? Math.max(0, document.querySelector('#AjaxCartForm .kt-alert').offsetTop - 100) : 0,
      behavior: 'smooth'
    });
  } else {
    document.querySelector('#site-cart-sidebar .kt-sidebar__content')?.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
});

/* Tabs */
function initTabs() {
  const tabWrappers = document.querySelectorAll('[data-tabs]');

  tabWrappers.forEach(wrapper => {
    const tabs = wrapper.querySelectorAll('[data-tab]');
    const panels = wrapper.querySelectorAll('[data-panel]');

    if (!tabs.length || !panels.length) return;

    // Prevent double binding
    if (wrapper.dataset.tabsInitialized) return;
    wrapper.dataset.tabsInitialized = 'true';

    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const id = tab.dataset.tab;

        // update tabs
        tabs.forEach(t => t.classList.remove('is-active'));
        tab.classList.add('is-active');

        // update panels
        panels.forEach(panel => {
          panel.classList.toggle('is-active', panel.dataset.panel === id);
        });
      });
    });
  });
}
document.addEventListener('DOMContentLoaded', () => {
  initTabs();
});
/* Product swatches */
function attachSwatchListeners() {
  const container = document.querySelector('.image-swatches-container');
  if (!container) return;
  container.removeEventListener('click', swatchClickHandler);
  container.addEventListener('click', swatchClickHandler);
}

function swatchClickHandler(e) {
  const swatch = e.target.closest('.swatch-button');
  if (!swatch) return;

  e.preventDefault();

  const productHandle = swatch.dataset.handle;
  const sectionId = swatch.dataset.sectionId;

  const newUrl = `/products/${productHandle}`;
  window.history.replaceState({ path: newUrl }, '', newUrl);

  const url = `/products/${productHandle}?section_id=${sectionId}`;

  fetch(url)
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to fetch section content');
      }
      return response.text();
    })
    .then(html => {
      const parser = new DOMParser();
      const newDoc = parser.parseFromString(html, 'text/html');

      // Elements to update
      const elementsToUpdate = [
        'product-page'
      ];

      elementsToUpdate.forEach(selector => {
        const newElement = newDoc.querySelector(selector);
        const currentElement = document.querySelector(selector);
        if (newElement && currentElement) {
          currentElement.replaceWith(newElement);
        }
      });

      // Reinitialize  third-party scripts below (TBD)

      /* Update TrustPilot */
      const trustPilotWidgets = document.querySelectorAll('.trustpilot-widget');
      if (!trustPilotWidgets) return;
      trustPilotWidgets.forEach(widget => {
        if (window.Trustpilot) {
          window.Trustpilot.loadFromElement(widget);
        }
      });

      const sizeGuideTrigger = document.querySelector('[aria-controls="size-guide-drawer"]');
      const sizeGuideDrawer = document.getElementById('size-guide-drawer');

      if (sizeGuideTrigger && sizeGuideDrawer) {
        sizeGuideTrigger.addEventListener('click', (e) => {
          e.preventDefault();

          sizeGuideTrigger.setAttribute('aria-expanded', 'true');
          sizeGuideDrawer.setAttribute('data-direction', 'right');
          sizeGuideDrawer.show();
        });
      }

      attachSwatchListeners();
      
    })
    .catch(error => {
      console.error('Error updating product section:', error);
    });
}
document.addEventListener('DOMContentLoaded', () => {
  attachSwatchListeners();
});

/* Size guide drawer tabs */
document.addEventListener('click', function(e) {
  const button = e.target.closest('.size-guide-drawer-button');

  if (!button) return;

  const drawer = button.closest('.kt-sidebar__body');

  const buttons = drawer.querySelectorAll('.size-guide-drawer-button');
  const tabs = drawer.querySelectorAll('.size-guide-drawer-tab');

  const tabId = button.dataset.tab;

  buttons.forEach(btn => {
    btn.classList.toggle('is-active', btn === button);
  });

  tabs.forEach(tab => {
    tab.classList.toggle(
      'is-active',
      tab.dataset.tab === tabId
    );
  });
});

function renderWishlistCounter(swat) {
	var counter_elem = document.querySelectorAll(".swym-wishlist-header-counter"); /* Targets the span added in Step 1. */
	if (counter_elem) {
		for (var i = 0; i < counter_elem.length; i++) {
			if (swat.renderWishlistCount) {
				swat.renderWishlistCount(counter_elem[i], function(count, element) {
					console.log("debug - renderWishlistCount", count, element); 
				});
			}
		}
	}
}
if (!window.SwymCallbacks) {
	window.SwymCallbacks = [];
}
window.SwymCallbacks.push(renderWishlistCounter);

/* Product grid slider */
function productGridSlider() {
  document.querySelectorAll('.kt-product-image-slider').forEach((slider, sliderIndex) => {
    const slides = [...slider.querySelectorAll('.kt-product-image__container')];

    if (slides.length <= 1) {
      slider.classList.add('is-single-image');
      return;
    }

    // Create track
    const track = document.createElement('div');
    track.className = 'slider-track';

    slides.forEach(slide => {
      track.appendChild(slide);
    });

    const progress = slider.querySelector('.slider-progress');
    const prevBtn = slider.querySelector('.slider-arrow--prev');
    const nextBtn = slider.querySelector('.slider-arrow--next');

    slider.insertBefore(track, progress);
    slider.classList.add('is-ready');
    let current = 0;
    let startX = 0;
    let pointerDown = false;
    const progressActive = slider.querySelector('.slider-progress__active');

    function update() {
      track.style.transform = `translateX(-${current * 100}%)`;
      prevBtn.hidden = current === 0;
      nextBtn.hidden = current === slides.length - 1;
      const width = 100 / slides.length;
      progressActive.style.width = `${width}%`;
      progressActive.style.transform = `translateX(${current * 100}%)`;
    }



    function goTo(index, source = 'user') {
      if (source !== 'hover') {
        hoverActivated = false;
      }

      current = Math.max(
        0,
        Math.min(index, slides.length - 1)
      );

      update();
    }

    if (
      slides.length > 1 &&
      slides[1].classList.contains('kt-hover-image') &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches
    ) {
      slider.closest('.kt-product-image').addEventListener('mouseenter', () => {
        if (current === 0) {
          hoverActivated = true;
          goTo(1, 'hover');
        }
      });

      slider.closest('.kt-product-image').addEventListener('mouseleave', () => {
        if (hoverActivated) {
          hoverActivated = false;
          goTo(0);
        }
      });
    }

    prevBtn.addEventListener('click', e => {
      e.preventDefault();
      goTo(current - 1);
    });

    nextBtn.addEventListener('click', e => {
      e.preventDefault();
      goTo(current + 1);
    });

    /* Swipe / drag support */

    const swipeArea = slider.closest('.kt-product-image ').querySelector('.kt-product-image__link');

    swipeArea.addEventListener('touchstart', e => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    swipeArea.addEventListener('touchend', e => {
      const diffX = e.changedTouches[0].clientX - startX;
      const diffY = e.changedTouches[0].clientY - startY;

      // Ignore vertical scrolling
      if (Math.abs(diffY) > Math.abs(diffX)) return;

      if (Math.abs(diffX) < 40) return;

      e.preventDefault();

      if (diffX < 0) {
        goTo(current + 1);
      } else {
        goTo(current - 1);
      }
    });

    track.addEventListener('pointercancel', () => {
      pointerDown = false;
    });
    update();

    /* First product peek animation */
    const grid = document.querySelector('#main-collection-product-grid');
    const firstGridSlider = grid?.querySelector('.kt-product-image-slider');
    if (slider === firstGridSlider) {
      const observer = new IntersectionObserver(entries => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        setTimeout(() => {
          track.style.transition = 'transform .6s ease';
          track.style.transform = 'translateX(-30%)';
          const width = 100 / slides.length;
          progressActive.style.width = `${width}%`;
          progressActive.style.transform = 'translateX(30%)';
          setTimeout(() => {
            track.style.transform = 'translateX(0)';
            progressActive.style.transform = 'translateX(0)';
            setTimeout(() => {
              update();
            }, 600);
          }, 1000);
        }, 500);
      }, {
        threshold: 0.5
      });
      observer.observe(slider);
    }

  });
}
document.addEventListener('DOMContentLoaded', () => {
  productGridSlider();
});

/* Blog tags - dynamic reload */
document.addEventListener('click', async (e) => {
  const tagLink = e.target.closest('.kt-blog-page_tags a');
  if (!tagLink) return;
  e.preventDefault();
  document
    .querySelectorAll('.kt-blog-page_tags .kt-label--selected')
    .forEach(el => el.classList.remove('kt-label--selected'));
  tagLink.classList.add('kt-label--selected');
  const url = tagLink.href;
  try {
    const res = await fetch(url);
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newSection = doc.querySelector('.blog-grid-section');
    const currentSection = document.querySelector('.blog-grid-section');
    if (newSection && currentSection) {
      currentSection.innerHTML = newSection.innerHTML;
    }
    window.history.pushState({}, '', url);
  } catch (err) {
    console.error('Failed to load blog tag content:', err);
  }
});

/* Collection - dynamic reload */
document.addEventListener('click', async (e) => {
  const card = e.target.closest('.template-collection .kt-card--collection');
  if (!card) return;

  e.preventDefault();

  const url = card.querySelector('.kt-collection-title a').href;
  if (!url) return;

  const res = await fetch(url);
  const html = await res.text();

  const doc = new DOMParser().parseFromString(html, 'text/html');

  const newGrid = doc.querySelector('.collection-grid-section');
  const currentGrid = document.querySelector('.collection-grid-section');

  if (newGrid && currentGrid) {
    currentGrid.innerHTML = newGrid.innerHTML;
  }

  window.history.pushState({}, '', url);

  document.body.append(document.getElementById('site-filters-sidebar'));
  productGridSlider();

  reinitCollection();
});

/* Rebuy ATC */
document.addEventListener('rebuy.add', async function(event) {
  event.preventDefault();
  console.log('rebuy.add event', event.detail);
  try {
    KROWN.functions.updateCart(true);
  } catch (err) {
    console.error('Error refreshing cart drawer after Rebuy add:', err);
  }
});